import axios from 'axios';

/** Shape of GET /api/spa-configuration (SpaConfigurationResponse.cs, camelCased). */
export interface MsalConfig {
  clientId: string;
  loginAuthority: string;
  changePasswordAuthority: string;
  knownAuthority: string;
  apiReadScope: string;
}

export interface SpaConfig {
  clientsApiUrl: string;
  gaCode?: string;
  appInsightConnectionString?: string;
  msalConfig: MsalConfig;
}

const STORAGE_KEY = 'spa.config';

let current: SpaConfig | null = null;

function isSpaConfig(value: unknown): value is SpaConfig {
  const v = value as SpaConfig | null;
  return !!v && typeof v.clientsApiUrl === 'string' && !!v.msalConfig?.clientId;
}

function readCached(): SpaConfig | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return isSpaConfig(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Loads the runtime configuration served by the ASP.NET host.
 * Cached per browser tab (sessionStorage) to avoid a round trip on every reload.
 */
export async function loadSpaConfig(): Promise<SpaConfig> {
  const cached = readCached();
  if (cached) {
    current = cached;
    return cached;
  }

  // Plain axios instance: no auth interceptors exist (or are needed) at this point.
  const { data } = await axios.create().get<SpaConfig>('/api/spa-configuration');
  if (!isSpaConfig(data)) {
    throw new Error('Invalid SPA configuration received from server');
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage unavailable (private mode / quota): keep the in-memory copy only.
  }
  current = data;
  return data;
}

export function spaConfig(): SpaConfig {
  if (!current) {
    throw new Error('SPA configuration has not been loaded yet');
  }
  return current;
}
